import { useEffect, useState } from "react";

/**
 * Captures the timestamp of the user's first interaction (click or keydown)
 * with the page. Returns `null` until an interaction has happened, then a
 * fixed `Date.now()` timestamp.
 *
 * Used by anti-spam (#538): when paired with a `submittedAt` timestamp on
 * submit, the server can drop submissions where the delta is impossibly
 * short (bots).
 *
 * Listeners are registered with `{ once: true }` and de-registered as soon
 * as any interaction fires, so the cost is one click/keydown listener per
 * mount and zero ongoing work afterward.
 */
export function useFirstInteraction(): number | null {
  const [timestamp, setTimestamp] = useState<number | null>(null);

  useEffect(() => {
    if (timestamp != null) return;
    if (typeof document === "undefined") return;

    const handler = () => {
      setTimestamp(Date.now());
      document.removeEventListener("click", handler);
      document.removeEventListener("keydown", handler);
    };

    document.addEventListener("click", handler, { once: true });
    document.addEventListener("keydown", handler, { once: true });

    return () => {
      document.removeEventListener("click", handler);
      document.removeEventListener("keydown", handler);
    };
  }, [timestamp]);

  return timestamp;
}
